import { makeAutoObservable } from "mobx";
import { ResourceState } from "./resourceState";

/**
 * Ленивая величина, загружаемая по ключу.
 *
 * Заменяет три независимые реализации из v1/v2 (детали объекта, значения
 * группы-справочника, «вещи» значения), у которых гарантии были разными:
 * дедупликация была только у первой, защита от поздних ответов — только у неё же,
 * а вторая молча глотала ошибку.
 *
 * Что гарантируется:
 * - на один ключ в один момент времени не более одного запроса;
 * - результат применяется, только если за время запроса ключ не был
 *   инвалидирован и не был начат новый запрос;
 * - ошибка сохраняется в состоянии и не мешает повторить попытку.
 *
 * Чего здесь намеренно нет: доменных знаний. Версия модели данных, язык и
 * пользователь — это часть ключа, а не опция ресурса. Политика «не перечитывать
 * при грязной форме» живёт в сторе. Отображение ошибки — во view.
 *
 * API заморожен на четырёх методах.
 */
export class Resource<K, T> {
  // Наблюдаемо только состояние: его читает представление.
  // Промисы, поколения и загрузчик — внутренняя кухня, реактивность им не нужна.
  private readonly states = new Map<string, ResourceState<T>>();

  private readonly pending = new Map<string, Promise<T>>();

  private readonly generations = new Map<string, number>();

  constructor(
    private readonly load: (key: K) => Promise<T>,
    private readonly keyOf: (key: K) => string,
  ) {
    makeAutoObservable<
      Resource<K, T>,
      "generations" | "keyOf" | "load" | "pending"
    >(this, {
      pending: false,
      generations: false,
      load: false,
      keyOf: false,
    });
  }

  /** Единственное место записи состояния: остальные методы зовут его. */
  private setState(id: string, next: ResourceState<T> | null) {
    if (next) this.states.set(id, next);
    else this.states.delete(id);
  }

  state(key: K): ResourceState<T> {
    return this.states.get(this.keyOf(key)) ?? { status: "none" };
  }

  /**
   * Значение по ключу: из памяти либо из загрузки.
   * Параллельные вызовы по одному ключу разделяют один запрос.
   *
   * Промис отдаёт загруженное значение всегда, даже если его результат был
   * отброшен как устаревший: данные есть данные. Решение, можно ли их
   * использовать, принимает вызывающая сторона по своему токену актуальности.
   */
  get(key: K): Promise<T> {
    const id = this.keyOf(key);
    const state = this.states.get(id);
    if (state?.status === "ready") return Promise.resolve(state.value);

    const pending = this.pending.get(id);
    if (pending) return pending;

    const generation = this.nextGeneration(id);
    this.setState(id, { status: "wait" });

    const request = this.load(key)
      .then((value) => {
        this.applyIfActual(id, generation, { status: "ready", value });
        return value;
      })
      .catch((error) => {
        this.applyIfActual(id, generation, { status: "error", error });
        throw error;
      })
      .finally(() => {
        if (this.pending.get(id) === request) this.pending.delete(id);
      });

    this.pending.set(id, request);
    return request;
  }

  /** Забыть значение. Ответ уже выполняющегося запроса будет отброшен. */
  invalidate(key: K) {
    const id = this.keyOf(key);
    this.nextGeneration(id);
    this.pending.delete(id);
    this.setState(id, null);
  }

  /**
   * Положить значение, полученное иным путём: создание, копирование, импорт.
   * Выполняющийся запрос по этому ключу отбрасывается — он заведомо старее.
   */
  set(key: K, value: T) {
    const id = this.keyOf(key);
    this.nextGeneration(id);
    this.pending.delete(id);
    this.setState(id, { status: "ready", value });
  }

  /**
   * Забыть всё. Нужно при смене пользователя: данные прежнего показывать
   * нельзя, а ключей мы снаружи не знаем.
   */
  clear() {
    this.states.clear();
    this.pending.clear();
    this.generations.clear();
  }

  private nextGeneration(id: string): number {
    const next = (this.generations.get(id) ?? 0) + 1;
    this.generations.set(id, next);
    return next;
  }

  private applyIfActual(
    id: string,
    generation: number,
    next: ResourceState<T>,
  ) {
    if (this.generations.get(id) !== generation) return;
    this.setState(id, next);
  }
}
